import { useEffect, useRef, useState } from 'react';

export default function WhyWebloom() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const reasons = [
    {
      icon: 'ri-rocket-line',
      title: 'Fast Delivery',
      description: 'Your website online in days, not weeks',
      gradient: 'from-[#FF6B6B] to-[#ff8f8f]'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Zero Risk',
      description: 'Pay only after seeing your completed website',
      gradient: 'from-[#0A1628] to-[#1a3a5c]'
    },
    {
      icon: 'ri-key-line',
      title: 'You Own It',
      description: 'Full ownership, your domain, your control',
      gradient: 'from-[#FF6B6B] to-[#ff8f8f]'
    }
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #0A1628 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Floating elements */}
      <div className="absolute top-20 left-10 md:left-20 w-16 md:w-20 h-16 md:h-20 border-2 border-[#FF6B6B]/20 rounded-full animate-pulse"></div>
      <div className="absolute bottom-20 right-10 md:right-20 w-12 md:w-16 h-12 md:h-16 bg-[#0A1628]/5 rounded-2xl rotate-12 animate-bounce" style={{ animationDuration: '5s' }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-20">
        <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-2 bg-[#FF6B6B]/10 text-[#FF6B6B] px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full"></span>
            Why me
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-[#0A1628] mb-4">
            Why choose<br />
            <span className="text-[#FF6B6B] italic">Webloom</span>?
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 lg:gap-12">
          {reasons.map((reason, index) => (
            <div 
              key={index} 
              className={`text-center space-y-4 md:space-y-6 group transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className={`w-24 h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-gradient-to-br ${reason.gradient} rounded-2xl md:rounded-[32px] flex items-center justify-center mx-auto shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                <i className={`${reason.icon} text-4xl md:text-5xl lg:text-6xl text-white`}></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#0A1628]">
                {reason.title}
              </h3>
              <p className="text-base md:text-lg text-[#0A1628]/60 leading-relaxed max-w-xs mx-auto px-4">
                {reason.description}
              </p>
            </div>
          ))}
        </div>

        {/* Trust badge */}
        <div className={`mt-12 md:mt-16 lg:mt-20 flex justify-center transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-3 md:gap-4 bg-white shadow-lg rounded-full px-6 md:px-8 py-3 md:py-4 border border-gray-100">
            <div className="flex -space-x-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-[#FF6B6B] to-[#ff8f8f] rounded-full border-2 border-white flex items-center justify-center">
                  <i className="ri-user-smile-line text-white text-xs md:text-sm"></i>
                </div>
              ))}
            </div>
            <div>
              <p className="font-semibold text-[#0A1628] text-sm md:text-base">50+ satisfied clients</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <i key={i} className="ri-star-fill text-[#FFB800] text-xs md:text-sm"></i>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
